import { AuthUserPermission, EngagementTaskTrait, ResourceDetailSource } from '@cibo/core'
import { Can } from '@cibo/profile'
import { AutoField, AutoFields, CiboAutoForm, debugSx, SubmitField } from '@cibo/ui'
import { Box, Button, Divider, Stack, StackProps, Typography } from '@mui/material'
import { ComponentProps, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useProgramEngagementContext } from '../../../../context'
import { ActionRequestButton } from './ActionRequestButton'
import { Aop1236Preview } from './Aop1236Preview'
import { CiboAutoFormDebugger } from './DataEntrySchemaEditingProvider/CiboAutoFormDebugger'
import { componentDetector } from './DataEntrySchemaEditingProvider/componentDetector'
import { applyReplacerToSchema, createYearTokenContext } from './schemaTokenContext'
type SingleTargetSchemaEditorProps = {
  traitStatus?: EngagementTaskTrait
  handleSaveTraitDetail: (detail: { value: any }) => void | Promise<any>
  traitDetailValue: any
  existingDetailSource?: ResourceDetailSource
  immutable?: boolean
  variant?: 'compact' | 'comfortable'
  DeleteButton?: ReactNode
  submitButtonProps?: Omit<ComponentProps<typeof SubmitField>, 'name'>
  fieldWrapperProps?: StackProps
  buttonWrapperProps?: StackProps
  onSuccess?: () => void
  year?: number
  'data-testid'?: string
  /** Render a display descriptor ({type, params}) embedded inside the schema form. */
  renderEmbeddedDisplay?: (field: any) => ReactNode
}

const DisplayComponents: Record<any, any> = {
  Box,
  Stack,
  ActionRequestButton,
  Aop1236Preview,
}

export const SingleTargetSchemaEditor = ({
  traitStatus,
  handleSaveTraitDetail,
  traitDetailValue,
  existingDetailSource,
  immutable,
  variant,
  DeleteButton,
  submitButtonProps,
  fieldWrapperProps,
  buttonWrapperProps,
  onSuccess,
  year,
  'data-testid': dataTestId,
  renderEmbeddedDisplay,
}: SingleTargetSchemaEditorProps) => {
  const isCompact = variant === 'compact'

  const { engagement } = useProgramEngagementContext()
  const [model, setModel] = useState<{ value: any }>()
  const [isLoading, setIsLoading] = useState(false)
  const tokenContext = useMemo(() => createYearTokenContext(year), [year])
  const rawSchema = traitStatus?.schema
  const schema = useMemo(
    () =>
      rawSchema && engagement.data
        ? applyReplacerToSchema(rawSchema, s => engagement.data.replaceCopyTokens(s))
        : rawSchema,
    [rawSchema, engagement.data]
  )

  useEffect(() => {
    if (traitDetailValue) {
      setModel({ value: traitDetailValue })
    }
  }, [traitDetailValue])

  const handleSubmit = useCallback(
    (model: any) => {
      setIsLoading(true)
      const result = handleSaveTraitDetail(model)
      if (result) {
        return Promise.resolve(result)
          .then(() => {
            onSuccess?.()
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
      onSuccess?.()
      setIsLoading(false)
    },
    [handleSaveTraitDetail, onSuccess]
  )

  // Apply schema modifications to hide "Value" title if present
  const modifiedSchema = useMemo(() => {
    if (!schema) return schema
    const modified = { ...schema }
    // If there's a "value" property in the schema, ensure its title is hidden
    if (modified.properties?.value) {
      modified.properties = {
        ...modified.properties,
        value: {
          ...modified.properties.value,
          'ui-params': {
            ...modified.properties.value['ui-params'],
            hideTitle: true,
          },
        },
      }
    }
    return modified
  }, [schema])

  if (!traitStatus?.schema) {
    return null
  }

  const uiParams = schema?.['ui-params'] || {}

  const renderDisplay = ({ field, itemProps }: { field: any; itemProps?: any }) => {
    if (typeof field === 'string') {
      return <AutoField key={field} name={field} {...itemProps} />
    }

    const Component = DisplayComponents[field.component]

    if (Component) {
      return (
        <Component key={field.name} {...itemProps} {...field.props}>
          {field.children?.map((child: any) => renderDisplay({ field: child }))}
        </Component>
      )
    }

    if (field.type) {
      return renderEmbeddedDisplay ? renderEmbeddedDisplay(field) : <>{JSON.stringify(field)}</>
    }

    return <>{JSON.stringify(field)}</>
  }

  return (
    <AutoField.componentDetectorContext.Provider value={componentDetector}>
      {year !== undefined && (
        <Divider sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {year}
          </Typography>
        </Divider>
      )}
      <CiboAutoForm
        schema={modifiedSchema ?? traitStatus.schema}
        model={model}
        onSubmit={handleSubmit}
        onChangeModel={setModel}
        tokenContext={tokenContext}
        disabled={immutable}
        validate={existingDetailSource && existingDetailSource !== 'user' ? 'onSubmit' : 'onChange'}
        autosave={uiParams?.autosave}
        warningSchema={
          uiParams?.warningSchema && {
            type: 'object',
            properties: { value: uiParams?.warningSchema },
          }
        }
        renderDisplay={renderDisplay}
        data-testid={dataTestId}
      >
        {/* @ts-ignore */}
        <Stack spacing={isCompact ? 1 : 2} {...fieldWrapperProps}>
          <AutoFields />
        </Stack>

        {!uiParams?.autosave && (
          <Stack
            {...buttonWrapperProps}
            sx={{ ml: 'auto', width: 'fit-content', ...buttonWrapperProps?.sx }}
          >
            <SubmitField
              data-testid={`submit-button-${traitStatus?.traitId}`}
              loading={isLoading}
              disabled={existingDetailSource && existingDetailSource !== 'user' ? false : undefined}
              {...submitButtonProps}
            />
            {DeleteButton}
            <Can useAny={AuthUserPermission.DEBUG_USER}>
              <Button onClick={() => handleSubmit(model)} sx={debugSx}>
                Submit sans validation
              </Button>
            </Can>
          </Stack>
        )}

        <CiboAutoFormDebugger />
      </CiboAutoForm>
    </AutoField.componentDetectorContext.Provider>
  )
}
